import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { ICustomer } from './Customer.model';
import { useDispatch, useSelector } from 'react-redux';
import { requestClearImageUpload, selectImageUpload } from '~/store/app';
import { useRequestCreateCustomer } from '~/requests';
import { CreateCustomerForm } from './CreateCustomerForm';
import { useProvider, useNavigation } from '~/Hooks';
import { bindActionCreators } from 'redux';
import { useRequestInviteCustomer } from '~/requests/coreService/useRequestInviteCustomer';
import { requestCustomer } from '~/store/bookings';
import { requestCustomerById } from '~/store';
import { Route } from '~/Constants/Routes.constants';
import { routeReplacer } from '~/Helpers/String.helper';

interface IUseCustomerCreation {
  setIsOpen: (isOpen: boolean) => void;
}

export const useCustomerCreation = ({ setIsOpen }: IUseCustomerCreation) => {
  const dispatch = useDispatch();
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const uploadedImage = useSelector(selectImageUpload);
  const clearImage = bindActionCreators(requestClearImageUpload, dispatch);
  const selectCreatedCustomer = bindActionCreators(requestCustomer, dispatch);
  const loadCustomer = bindActionCreators(requestCustomerById, dispatch);
  const { navigate } = useNavigation();

  const { currentProvider } = useProvider();

  const initialValues = {
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    sendInvite: false,
  };
  const [values, setValues] = useState(initialValues);

  const { mutate: createCustomer, data, isSuccess } = useRequestCreateCustomer();
  const { mutate: inviteCustomer } = useRequestInviteCustomer();

  const onSave = () => {
    const customerFormatted = {
      domain: currentProvider?.domain || '',
      firstname: values.firstname,
      lastname: values.lastname,
      email: values.email || '',
      phone: values.phone || '',
      imageId: uploadedImage.id || '',
    };

    createCustomer(customerFormatted);
  };

  const validate = useCallback(
    (values: ICustomer) => {
      const shouldDisable = values.firstname === '' || values.lastname === '';
      setIsSubmitDisabled(shouldDisable);
      setValues(values);
    },
    [setValues, setIsSubmitDisabled],
  );

  const sections = useMemo(() => [() => <CreateCustomerForm />], []);

  const handleFinishUserCreation = (customerId: string) => {
    const newCustomer = loadCustomer(customerId);
    selectCreatedCustomer(newCustomer as any);

    navigate(
      routeReplacer(Route.CUSTOMER_PROFILE, {
        customerId,
        domain: currentProvider?.domain || '',
      }),
    );
    setIsOpen(false);
  };

  useEffect(() => {
    //clear image in the first load
    clearImage();

    return () => {
      clearImage();
    };
  }, []);

  useEffect(() => {
    const sendInviteAndSelectCustomer = () => {
      const customerId = data?.customerId;

      if (isSuccess && customerId) {
        if (values.sendInvite && values.email) {
          inviteCustomer(customerId);
        }
        handleFinishUserCreation(customerId);
      }
    };

    sendInviteAndSelectCustomer();
  }, [data?.customerId, isSuccess, values.email, values.sendInvite]);

  return {
    onSave,
    initialValues,
    validate,
    isSubmitDisabled,
    error: false,
    isLoading: false,
    imageUrl: uploadedImage.url,
    listItems: sections,
  };
};
